import { useMemo, useState, type FormEvent } from 'react'
import { Lightbulb, Plus } from 'lucide-react'
import {
	type GroceryItem,
	type GroceryListResponse,
	type GroceryTitle,
	groceryTitleKey,
} from '@hewey/shared'
import { api } from '@api'
import { ConfirmDialog } from '@components/ConfirmDialog.tsx'
import { Header } from '@components/Header.tsx'
import { IconToggle } from '@components/IconToggle.tsx'
import { useFavicon } from '@hooks/useFavicon.ts'
import { useOnline } from '@hooks/useOnline.ts'
import { useResource } from '@hooks/useResource.ts'
import { useWakeLock, wakeLockSupported } from '@hooks/useWakeLock.ts'
import { useMode } from '@mode/ModeContext.tsx'
import { sharesViaSheet, shareText } from '@utils/shareText.ts'
import { ModeToggle } from '@mode/ModeToggle.tsx'
import { GroceryRow } from './GroceryRow.tsx'
import { dropTicks, flushTicks, queueTick, useTickQueue } from './tickQueue.ts'
import styles from './Groceries.module.css'

const plural = (n: number, one: string, many: string) =>
	`${n} ${n === 1 ? one : many}`

export const GroceriesApp = () => {
	useFavicon('groceries')
	const { mode } = useMode()
	const online = useOnline()
	// The family list is shared, so poll to see each other's ticks.
	const groceries = useResource(
		['groceries', mode],
		() => api.grocery.list(mode),
		{ pollIntervalMs: mode === 'family' ? 10_000 : undefined },
	)

	const withChange =
		(id: GroceryItem['id'], change: Partial<GroceryItem>) =>
		(prev: GroceryListResponse | undefined) =>
			prev
				? {
						items: prev.items.map((g) =>
							g.id === id ? { ...g, ...change } : g,
						),
					}
				: prev

	const queue = useTickQueue((sent) =>
		groceries.set(withChange(sent.id, sent)),
	)

	const [wakeLock, setWakeLock] = useState(false)
	useWakeLock(wakeLock)

	const [draft, setDraft] = useState('')
	const [notice, setNotice] = useState<string | null>(null)
	const [confirmOpen, setConfirmOpen] = useState(false)
	const [copied, setCopied] = useState(false)

	// Held ticks win over the server's copy until they've been sent.
	const items = useMemo(
		() =>
			(groceries.data?.items ?? []).map((g) => {
				const held = queue.pending[g.id]
				return held === undefined || held === g.ticked
					? g
					: { ...g, ticked: held }
			}),
		[groceries.data, queue.pending],
	)
	const toGet = items.filter((g) => !g.ticked)
	const ticked = items.filter((g) => g.ticked)
	const heldCount = Object.keys(queue.pending).length

	const onAdd = async (e: FormEvent) => {
		e.preventDefault()
		const title = draft.trim().slice(0, 200)
		if (title === '') return
		setDraft('')

		const existing = items.find(
			(g) => groceryTitleKey(g.title) === groceryTitleKey(title),
		)
		if (existing && !existing.ticked) {
			setNotice(`${existing.title} is already on the list.`)
			return
		}
		if (existing) {
			queueTick(existing.id, false)
			setNotice(`${existing.title} was ticked, and is back on the list.`)
			return
		}

		setNotice(null)
		try {
			const res = await api.grocery.create({
				mode,
				title: title as GroceryTitle,
			})
			// A poll can land between the save and this response, and already
			// hold the item.
			groceries.set((prev) => ({
				items: [
					res.item,
					...(prev?.items ?? []).filter((g) => g.id !== res.item.id),
				],
			}))
		} catch {
			// Hand the text back rather than lose it, unless the next item
			// is already being typed.
			setDraft((d) => (d === '' ? title : d))
			setNotice(
				'This item could not be added. Check your connection and try again.',
			)
		}
	}

	const onRename = (item: GroceryItem, title: GroceryTitle) =>
		groceries
			.mutate(withChange(item.id, { title }), () =>
				api.grocery.update(item.id, { title }),
			)
			.catch(() => {
				// The refetch after the write puts the old title back.
			})

	const onShare = async () => {
		const outcome = await shareText(
			toGet.map((g) => `• ${g.title}`).join('\n'),
		)
		if (outcome === 'copied') {
			setCopied(true)
			window.setTimeout(() => setCopied(false), 1500)
		} else if (outcome === 'failed') {
			setNotice('The list could not be copied.')
		}
	}

	const onDeleteTicked = async () => {
		setConfirmOpen(false)
		// Held ticks go first, so the server deletes what's on screen.
		await flushTicks()
		const ids = ticked.map((g) => g.id)
		dropTicks(ids)
		await groceries
			.mutate(
				(prev) =>
					prev
						? {
								items: prev.items.filter(
									(g) => !ids.includes(g.id),
								),
							}
						: prev,
				() => api.grocery.clearTicked({ mode }),
			)
			.catch(() => {
				// The refetch after the write shows whatever survived.
			})
	}

	return (
		<main className={styles.screen}>
			<Header
				left={
					wakeLockSupported() ? (
						<IconToggle
							on={wakeLock}
							onChange={setWakeLock}
							icon={Lightbulb}
							ariaLabel="Keep screen on"
						/>
					) : null
				}
				right={<ModeToggle />}
			/>

			<div className={styles.addBar}>
				<form className={styles.add} onSubmit={(e) => void onAdd(e)}>
					<input
						className={styles.addInput}
						value={draft}
						maxLength={200}
						placeholder="Add an item"
						aria-label="Add an item"
						enterKeyHint="enter"
						disabled={!online}
						onChange={(e) => {
							setDraft(e.target.value)
							setNotice(null)
						}}
					/>
					<button
						type="submit"
						className={styles.addButton}
						disabled={!online || draft.trim() === ''}
						aria-label="Add"
					>
						<Plus size={20} aria-hidden="true" />
					</button>
				</form>
				{notice ? (
					<p className={styles.notice} role="status">
						{notice}
					</p>
				) : null}
				{heldCount > 0 && (queue.stalled || !online) ? (
					<p className={styles.notice} role="status">
						{plural(heldCount, 'tick is', 'ticks are')} saved on
						this device, and will be sent when the connection
						returns.
					</p>
				) : null}
			</div>

			{groceries.error ? (
				<p className={styles.empty}>
					The list could not be loaded.{' '}
					<button
						type="button"
						className={styles.retry}
						onClick={() => void groceries.refetch()}
					>
						Retry
					</button>
				</p>
			) : groceries.loading && items.length === 0 ? (
				<p className={styles.empty}>Loading…</p>
			) : items.length === 0 ? (
				<p className={styles.empty}>
					Nothing on the list yet. Add an item above.
				</p>
			) : (
				<>
					{toGet.length > 0 ? (
						<section className={styles.section}>
							<div className={styles.sectionHead}>
								<h2 className={styles.heading}>
									To get ({toGet.length})
								</h2>
								<button
									type="button"
									className={styles.sectionAction}
									onClick={() => void onShare()}
								>
									{copied
										? 'Copied'
										: sharesViaSheet()
											? 'Share list'
											: 'Copy list'}
								</button>
							</div>
							<ul className={styles.list}>
								{toGet.map((g) => (
									<GroceryRow
										key={g.id}
										item={g}
										onTick={(next) => queueTick(g.id, next)}
										onRename={(title) =>
											void onRename(g, title)
										}
									/>
								))}
							</ul>
						</section>
					) : (
						<p className={styles.empty}>
							Everything on the list is ticked.
						</p>
					)}

					{ticked.length > 0 ? (
						<section className={styles.section}>
							<div className={styles.sectionHead}>
								<h2 className={styles.heading}>
									Ticked ({ticked.length})
								</h2>
								<button
									type="button"
									className={styles.sectionAction}
									onClick={() => setConfirmOpen(true)}
									disabled={!online}
								>
									Delete ticked items
								</button>
							</div>
							<ul className={styles.list}>
								{ticked.map((g) => (
									<GroceryRow
										key={g.id}
										item={g}
										onTick={(next) => queueTick(g.id, next)}
										onRename={(title) =>
											void onRename(g, title)
										}
									/>
								))}
							</ul>
						</section>
					) : null}
				</>
			)}

			<ConfirmDialog
				open={confirmOpen}
				title="Delete ticked items"
				body={
					<p>
						This will permanently remove{' '}
						{plural(ticked.length, 'ticked item', 'ticked items')}{' '}
						from the {mode === 'family' ? 'family' : 'personal'}{' '}
						list.
					</p>
				}
				confirmLabel="Delete"
				destructive
				onConfirm={() => void onDeleteTicked()}
				onClose={() => setConfirmOpen(false)}
			/>
		</main>
	)
}
